import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import * as R from 'ramda';

import { FrequencyTypes, dateFunctionOptions } from '../../strings';

import { NotificationSlice } from 'notification/exports';

// components
import {
	Box,
	Description,
	InputContainer,
	Loader,
	Input,
	DatePicker,
	DateTimePicker,
	Select,
	SelectContainer,
	SelectOption,
	SelectOptionList,
	SelectPlaceholder,
	DateRange,
	Tag,
	TagInput,
	Tags,
	Text,
	IconButton,
	IInputCallbackData,
	IRangeSelection,
	Label,
	SelectIcon,
	TagText,
	SelectFilter,
} from '@moosend/mooskin';

// styles
import styles from './styles/Filter.css';

// models
import { IConditionButton, IConditionFilter, IFilterSelectOptions, IFieldOptions, IWebsites, IConditionSuggestion } from '../../model';

import { isBetweenComparer, getDateTransformOptions, isSpecialDateTransform } from '../../helpers';
import { IAccountPreferences } from '../../../../../accounts/src/model';
import { translateText } from '../../../util/translationHelper';
import { getGUIDWithoutDashes } from '../../../util/stringHelper';
import { IFilterCollection } from '../filterCollection/FilterCollection';
import { useTransifex } from '../../../util/commonHooks';

export interface IFilterProps extends IConditionFilter {
	criteriaOptions: IFilterSelectOptions[];
	style?: React.CSSProperties;
	className?: string;
	onConditionChange: (data: IInputCallbackData, id: string) => void;
	fieldOptions?: IFieldOptions;
	productCode?: string;
	productCategory?: string;
	productBrand?: string;
	website?: string;
	dateFunction?: string;
	timesFilter?: string;
	forSegments?: boolean;
	websites?: IWebsites[];
	dateFormat?: IAccountPreferences;
	isCustomValue?: boolean;
	isOldDate?: boolean;
	getSegmentSuggestions?: IFilterCollection['getSegmentSuggestions'];
	segmentType?: string;
}

const maxSuggestionWidth = 275;

export const Filter: React.SFC<IFilterProps> = (props) => {
	useTransifex();
	const productCodeDesc = 'Enter a product code or leave empty for any product.';
	const productBrandDesc = 'Select a product brand';
	const productCategoryDesc = 'Select a product category';
	const { fieldOptions } = props;
	const isNotBooleanComparer = props.comparer !== 'IsEmpty' && props.comparer !== 'IsNotEmpty';
	const containsDoesNotContainComparers = ['Contains', 'DoesNotContain', 'StartsWith', 'DoesNotStartWith', 'EndsWith', 'DoesNotEndWith'];
	const isContainsDoesNotContainComparer = containsDoesNotContainComparers.includes(props.comparer);
	const [stateSuggestions, setStateSuggestions] = React.useState(props.suggestions);
	let urlParams = useParams<{ id?: string; sid?: string }>();
	React.useEffect(() => {
		if (props.valueType === 'Text') {
			setStateSuggestions(props.suggestions);
		} else {
			setStateSuggestions(
				props.suggestions.map((item) => ({
					...item,
					Value: item.Value ? item.Value.toString() : '',
				}))
			);
		}
	}, []);
	React.useEffect(() => {
		searchCustomFieldOptions('');
	}, [props.criteria, props.suggestions, props.value, fieldOptions, urlParams.id]);

	const dispatch = useDispatch();

	const onConditionChange = (data: IInputCallbackData) => {
		// Ignore the disabled criteria when clicked
		if (props.fieldOptions?.IsDisabled && data.value === props.criteria) return;

		if (fieldOptions && !fieldOptions.IsProductFilterSupported && props.valueType !== 'Date' && data.dataLabel === 'value') {
			//const value: string[] = data.value ? props.value.split('|') : [];
			data = { ...data, value: Array.isArray(data.value) ? data.value.join('|') : data.value };
		}

		props.onConditionChange && props.onConditionChange(data, props.id);
	};

	const onTagAdd = (data: IInputCallbackData<string>) => {
		if (disableIfCriteriaEmpty(props.criteria)) return;
		if ((props.valueType === 'Decimal' || props.valueType === 'Integer') && isNaN(data.value as any))
			dispatch(
				NotificationSlice.actions.showNotification({
					message: 'Input type is invalid',
					type: 'error',
				})
			);
		else {
			const value: string[] = props.value ? props.value.split('|') : [];

			if (!value.map((item) => item.toLowerCase()).includes(data.value.toLowerCase())) value.push(data.value);

			const newData = {
				dataLabel: data.dataLabel,
				value: value.join('|'),
			};

			props.onConditionChange && props.onConditionChange(newData, props.id);
		}
	};

	const onTagBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value) {
			const data = {
				value,
				dataLabel: 'value',
			};
			onTagAdd(data);
		}
	};

	const onMultiSelectChange = (data: IInputCallbackData) => {
		const newData = {
			dataLabel: data.dataLabel,
			value: Array.isArray(data.value) ? data.value.join('|') : data.value,
		};

		props.onConditionChange && props.onConditionChange(newData, props.id);
	};

	const onTagRemove = (data: IInputCallbackData, index: number) => {
		if (disableIfCriteriaEmpty(props.criteria)) return;
		const value = props.value ? props.value.split('|') : [];
		value.splice(value.length - 1);

		const newData = {
			dataLabel: index.dataLabel,
			value: value.join('|'),
		};

		props.onConditionChange && props.onConditionChange(newData, props.id);
	};

	const onRangeChange = (data: { [key: string]: IRangeSelection }) => {
		const dateFormat = 'yyyy-MM-dd';
		const newValue = format(data.selection.startDate, dateFormat) + '|' + format(data.selection.endDate, dateFormat);

		onConditionChange({ dataLabel: 'value', value: newValue });
	};

	const onExtraRangeChange = (data: { [key: string]: IRangeSelection }) => {
		const dateFormat = 'yyyy-MM-dd';

		const newValue = {
			EndDate: format(data.selection.endDate, dateFormat),
			StartDate: format(data.selection.startDate, dateFormat),
		};
		onConditionChange({ dataLabel: 'dateBetween', value: newValue });
	};

	const onIntervalChange = (data: IInputCallbackData) => {
		let newValue = data.dataLabel && {
			...props.interval,
		};
		if (props.fieldOptions?.MinAllowedDate && data.dataLabel === 'type') {
			let timeSelectorFormat = 1;
			data.value.toString() === '3' && (timeSelectorFormat = 1000 * 3600 * 24); //days
			data.value.toString() === '2' && (timeSelectorFormat = 1000 * 3600); //hours
			data.value.toString() === '1' && (timeSelectorFormat = 1000 * 60); //minutes
			const minAllowedDays = props.fieldOptions?.MinAllowedDate
				? (new Date().getTime() - new Date(props.fieldOptions?.MinAllowedDate).getTime()) / timeSelectorFormat
				: props.interval.value;

			newValue = data.dataLabel && {
				...props.interval,
				type: data.value,
				value: Math.round(minAllowedDays) < props.interval.value ? Math.round(minAllowedDays) : props.interval.value,
			};
		} else {
			newValue = data.dataLabel && {
				...props.interval,
				[data.dataLabel]: data.value,
			};
		}
		onConditionChange({ dataLabel: 'interval', value: newValue });
	};

	const renderOptions = (options: { label: string; value: string; isDisabled: boolean }[]) => {
		return options
			?.filter((item) => item.isDisabled !== true || (item.isDisabled && props.criteria === item.value))
			.map((option, i) => {
				return (
					<SelectOption key={i} value={option.value} opacity={option.isDisabled && props.criteria === option.value ? 0.5 : 1}>
						{translateText(option.label)}
					</SelectOption>
				);
			});
	};

	const renderButton = (button: IConditionButton, index: number) => {
		return (
			<IconButton
				flex={1}
				key={index}
				{...button}
				onClick={(e: React.MouseEvent) => button.onButtonClick && button.onButtonClick(props.id, props.extraOption)}
				disabled={button.children === 'clear' ? false : disableIfCriteriaEmpty(props.criteria)}
			>
				{button.children}
			</IconButton>
		);
	};

	const renderButtonsContainer = (buttons: IConditionButton[]) => {
		return (
			<Box d="flex" justify="center" align={props.forSegments ? 'start' : 'center'} pt={props.forSegments ? 8 : undefined}>
				{buttons.map((button, i) => {
					return renderButton(button, i);
				})}
			</Box>
		);
	};

	const renderLoader = () => {
		return (
			<Box d="flex" flexGrow={1} align="center" justify="center">
				<Loader size={30} />
			</Box>
		);
	};

	const renderExtraOptions = () => {
		switch (props.extraOption) {
			case 'dateBetween':
				// const start = props.value.split('|')[0];
				// const end = props.value.split('|')[1];
				const start = props.dateBetween.StartDate;
				const end = props.dateBetween.EndDate;

				const dateFormatFromSettings = props.dateFormat ? props.dateFormat.DateFormat : 'yyyy-MM-dd';

				return (
					<Box position="relative" mt={10} d="flex" w={265} alignSelf="flex-end" mr={92}>
						<Label position="absolute" left={-115} top={10}>
							Date Between
						</Label>
						{props.fieldOptions?.MinAllowedDate ? (
							<DateRange
								onChange={onExtraRangeChange}
								format={dateFormatFromSettings}
								// dataLabel="dateBetween"
								wrapperProps={{ w: '100%' }}
								ranges={[
									{
										endDate: new Date(end),
										key: 'selection',
										startDate: props.fieldOptions?.MinAllowedDate
											? new Date(start) < new Date(props.fieldOptions?.MinAllowedDate)
												? new Date(props.fieldOptions?.MinAllowedDate)
												: new Date(start)
											: new Date(start),
									},
								]}
								minDate={props.fieldOptions?.MinAllowedDate ? new Date(props.fieldOptions?.MinAllowedDate) : null}
								disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
							/>
						) : (
							<DateRange
								onChange={onExtraRangeChange}
								format={dateFormatFromSettings}
								// dataLabel="dateBetween"
								wrapperProps={{ w: '100%' }}
								ranges={[
									{
										endDate: new Date(end),
										key: 'selection',
										startDate: new Date(start),
									},
								]}
								disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
							/>
						)}
					</Box>
				);
			case 'interval':
				let timeSelectorFormat;
				props.interval.type.toString() === '3' && (timeSelectorFormat = 1000 * 3600 * 24); //days
				props.interval.type.toString() === '2' && (timeSelectorFormat = 1000 * 3600); //hours
				props.interval.type.toString() === '1' && (timeSelectorFormat = 1000 * 60); //minutes
				const minAllowedDays = props.fieldOptions?.MinAllowedDate
					? (new Date().getTime() - new Date(props.fieldOptions?.MinAllowedDate).getTime()) / timeSelectorFormat
					: undefined;
				return (
					<Box position="relative" mt={10} d="flex" w={265} alignSelf="flex-end" mr={92}>
						<Label position="absolute" left={-85} top={10}>
							In the last
						</Label>
						<Input
							type="number"
							value={props.interval.value}
							w="40%"
							mr={10}
							min={0}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								if (/^(\s*|\d+)$/.test(e.target.value))
									onIntervalChange({
										value: minAllowedDays
											? parseInt(e.target.value) > Math.round(minAllowedDays)
												? Math.round(minAllowedDays)
												: e.target.value
											: e.target.value,
										dataLabel: 'value',
									});
							}}
							disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
						/>
						<Select
							w="60%"
							dataLabel="type"
							onChangeSelect={(e, data) => onIntervalChange({ dataLabel: data.dataLabel, value: parseInt(data.value, 10) })}
							selectedValue={props.interval.type.toString()}
							disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
						>
							<SelectContainer>
								<SelectPlaceholder>Select an option</SelectPlaceholder>
							</SelectContainer>
							<SelectOptionList>
								{FrequencyTypes.map((item) => {
									return (
										<SelectOption key={item.value} value={item.value}>
											{item.label}
										</SelectOption>
									);
								})}
							</SelectOptionList>
						</Select>
					</Box>
				);

			default:
				break;
		}
	};

	const searchCustomFieldOptions = async (keyword: string | null) => {
		let suggestions = [...props.suggestions];
		const mailingListId = urlParams.id;

		// Optional "any product" first item
		if (fieldOptions?.IsProductFilterSupported) {
			suggestions.unshift({
				Key: '',
				Value: 'any',
				Label: 'with any product code',
			});
		}

		const valueLabel = fieldOptions?.IsProductFilterSupported ? 'productCode' : 'value';
		const valueToShow =
			valueLabel === 'value' && !Array.isArray(props[valueLabel])
				? props[valueLabel]
					? props[valueLabel].split('|')
					: []
				: props[valueLabel];

		if (props.getSegmentSuggestions) {
			try {
				const response = await props.getSegmentSuggestions(props.criteria, { mailingListId, keyword });

				// Map server response to array of { Key, Value }
				suggestions = response.data.Context.map((item) => ({
					Key: item.Key,
					Value: item.Value,
					Label: item.Label || item.Value,
				}));

				// Deduplicate by Key
				const existingKeys = new Set(suggestions.map((item) => item.Key));
				const newSuggestionsItems = (valueToShow || [])
					.filter((item) => !existingKeys.has(item))
					.map((item) => ({ Key: item, Value: item, Label: item }));

				// New items on top
				suggestions = [...newSuggestionsItems, ...suggestions];

				setStateSuggestions(suggestions);
			} catch (err) {
				console.error('Error fetching suggestions:', err);
			}
		} else {
			setStateSuggestions(suggestions);
		}
	};

	const renderSuggestions = () => {
		if (props.comparer === 'IsEmpty' || props.comparer === 'IsNotEmpty') return;

		const suggestions = [...props.suggestions];

		if (fieldOptions && fieldOptions.IsProductFilterSupported)
			suggestions.splice(0, 0, {
				Key: '',
				Value: 'any',
				Label: 'with any product code',
			});

		const valueLabel = fieldOptions && fieldOptions.IsProductFilterSupported ? 'productCode' : 'value';
		const valueToShow =
			valueLabel === 'value' && !Array.isArray(props[valueLabel])
				? props[valueLabel]
					? props[valueLabel].split('|')
					: []
				: props[valueLabel];

		const tags = props.value ? props.value.split('|') : [];

		return (
			<>
				{props.valueType === 'Text' ? (
					<ValueWrapper>
						<Select
							flex={1}
							dataLabel={valueLabel}
							onChangeSelect={(e, data) => onConditionChange(data)}
							selectedValue={!props[valueLabel] && fieldOptions?.IsProductFilterSupported ? 'any' : valueToShow}
							disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
						>
							<SelectContainer>
								<SelectPlaceholder maxW={props.buttons && props.buttons.length > 1 ? 213 : 275}>Select an option</SelectPlaceholder>
								<SelectFilter onChange={(e) => searchCustomFieldOptions(e.target.value)} />
							</SelectContainer>
							<SelectOptionList className="notranslate">
								{stateSuggestions.length > 0 &&
									stateSuggestions.map((item, i) => {
										const label = item.Label ? item.Label : item.Value;
										const value =
											(props.valueType === 'Guid' && props.criteria !== 'LinkURL') ||
											props.valueType === 'Enum' ||
											item.Key === '__supports_custom_value__'
												? item.Key
												: item.Value;
										return (
											<SelectOption key={i} value={value}>
												{label}
											</SelectOption>
										);
									})}
								{stateSuggestions.length === 0 &&
									suggestions.map((item, i) => {
										const label = item.Label ? item.Label : item.Value;
										const value =
											(props.valueType === 'Guid' && props.criteria !== 'LinkURL') ||
											props.valueType === 'Enum' ||
											item.Key === '__supports_custom_value__'
												? item.Key
												: item.Value;
										return (
											<SelectOption key={i} value={value}>
												{label}
											</SelectOption>
										);
									})}
							</SelectOptionList>
						</Select>
						{valueLabel === 'productCode' && <Description>{productCodeDesc}</Description>}
						{renderProductBrand()}
						{renderProductCategory()}
						{renderHowMany()}
					</ValueWrapper>
				) : (
					<ValueWrapper>
						{!isContainsDoesNotContainComparer ? (
							<>
								<Select
									flex={1}
									dataLabel={valueLabel}
									onChangeSelect={(e, data) => onConditionChange(data)}
									selectedValue={!props[valueLabel] && fieldOptions?.IsProductFilterSupported ? 'any' : valueToShow}
									disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
								>
									<SelectContainer>
										<SelectPlaceholder maxW={props.buttons && props.buttons.length > 1 ? 213 : 275}>Select an option</SelectPlaceholder>
									</SelectContainer>
									<SelectOptionList className="notranslate">
										{suggestions.map((item, i) => {
											const label = item.Label ? item.Label : item.Value;
											const value =
												(props.valueType === 'Guid' && props.criteria !== 'LinkURL') ||
												props.valueType === 'Enum' ||
												item.Key === '__supports_custom_value__'
													? item.Key
													: item.Value;
											return (
												<SelectOption key={i} value={value}>
													{label}
												</SelectOption>
											);
										})}
									</SelectOptionList>
								</Select>
								{valueLabel === 'productCode' && <Description>{productCodeDesc}</Description>}
								{renderProductBrand()}
								{renderProductCategory()}
								{renderHowMany()}
							</>
						) : (
							<Box position="relative" style={{ marginBottom: '15px' }}>
								<InputContainer flex={1} disabled={props.segmentType?.includes('SystemGenerated')}>
									<Tags onAddTag={(data) => onTagAdd(data)} onRemoveTag={(e, data) => onTagRemove(data, data.value)} dataLabel="value">
										{tags.map((item: string) => {
											return (
												<Tag key={item} disabled={props.segmentType?.includes('SystemGenerated')}>
													<TagText>{item}</TagText>
												</Tag>
											);
										})}
										<TagInput
											pl={tags.length ? 10 : 0}
											onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
											disabled={disableIfCriteriaEmpty(props.criteria)}
											onBlur={onTagBlur}
										/>
									</Tags>
								</InputContainer>
							</Box>
						)}
					</ValueWrapper>
				)}
			</>
		);
	};

	const renderDatePicker = (isDate?: boolean) => {
		const dateFormatFromSettings = props.dateFormat
			? isDate
				? `${props.dateFormat.DateFormat}`
				: `${props.dateFormat.DateFormat} ${props.dateFormat.TimeFormat?.replace('tt', 'a')}`
			: 'yyyy-MM-dd';

		if (isBetweenComparer(props.comparer)) {
			const start = props.value.split('|')[0];
			const end = props.value.split('|')[1];

			return (
				<DateRange
					wrapperProps={{ w: '100%' }}
					onChange={onRangeChange}
					format={dateFormatFromSettings}
					// dataLabel="value"
					ranges={[
						{
							endDate: new Date(end),
							key: 'selection',
							startDate: new Date(start),
						},
					]}
				/>
			);
		}

		const onDateChange = (data: Date) => {
			onConditionChange({
				dataLabel: 'value',
				value: isDate ? format(data, 'yyyy-MM-dd') : format(data, 'yyyy-MM-dd HH:mm:00'),
			});
		};

		const dateValue = props.value && !props.isOldDate ? (isDate ? props.value : zonedTimeToUtc(props.value, '')) : '';

		if (isDate) {
			const valueParts = props.value.split('-');

			return (
				<DatePicker
					value={
						valueParts[0].length < 4 ? props.value : new Date(parseInt(valueParts[0]), parseInt(valueParts[1]) - 1, parseInt(valueParts[2]))
					}
					onChange={onDateChange}
					format={dateFormatFromSettings}
					disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
				/>
			);
		}

		return <DateTimePicker value={dateValue} onChange={onDateChange} format={dateFormatFromSettings} />;
	};

	const renderProductCode = () => {
		let component;
		if (fieldOptions && fieldOptions.IsProductFilterSupported) {
			const valueLabel = fieldOptions && fieldOptions.IsProductFilterSupported ? 'productCode' : 'value';
			const valueProp = fieldOptions && fieldOptions.IsProductFilterSupported ? props.productCode : props.value;
			const placeholder = fieldOptions && fieldOptions.IsProductFilterSupported ? 'with product code...' : '';
			const description = fieldOptions && fieldOptions.IsProductFilterSupported ? productCodeDesc : '';
			component = (
				<Box position="relative" style={{ marginBottom: '15px' }}>
					<Input
						onChange={(e: any) => onConditionChange({ value: e.target.value, dataLabel: valueLabel })}
						value={valueProp}
						type="text"
						id="filter-integer-input"
						placeholder={placeholder}
						disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
					/>
					<Description>{description}</Description>
					{props.isCustomValue && (
						<SelectIcon
							position="absolute"
							top={8}
							right={0}
							cursor="pointer"
							onClick={() => onConditionChange({ dataLabel: 'closeSuggestions', value: !props.isCustomValue })}
						>
							keyboard_arrow_down
						</SelectIcon>
					)}
				</Box>
			);
		}
		return component;
	};
	const renderProductBrand = () => {
		let component;
		if (fieldOptions && fieldOptions.IsBrandFilterSupported) {
			const mlistId = urlParams.id;

			const valueLabel = fieldOptions && fieldOptions.IsBrandFilterSupported ? 'productBrand' : 'value';
			const valueProp = fieldOptions && fieldOptions.IsBrandFilterSupported ? props.productBrand : props.value;
			const placeholder = fieldOptions && fieldOptions.IsBrandFilterSupported ? 'for product brand...' : '';
			const description = fieldOptions && fieldOptions.IsBrandFilterSupported ? productBrandDesc : '';
			component = (
				<ExtraOptionsSelect
					valueLabel={valueLabel}
					valueProp={valueProp}
					mailingListId={mlistId}
					buttons={props.buttons}
					onConditionChange={onConditionChange}
					criteria={props.criteria}
					property="ProductBrand"
					placeholder={placeholder}
					isDisabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
					getSegmentSuggestions={props.getSegmentSuggestions}
				/>
			);
		}
		return component;
	};
	const renderProductCategory = () => {
		let component;
		if (fieldOptions && fieldOptions.IsCategoryFilterSupported) {
			const mlistId = urlParams.id;
			const valueLabel = fieldOptions && fieldOptions.IsCategoryFilterSupported ? 'productCategory' : 'value';
			const valueProp = fieldOptions && fieldOptions.IsCategoryFilterSupported ? props.productCategory : props.value;
			const placeholder = fieldOptions && fieldOptions.IsCategoryFilterSupported ? productCategoryDesc : '';
			component = (
				<ExtraOptionsSelect
					valueLabel={valueLabel}
					valueProp={valueProp}
					mailingListId={mlistId}
					buttons={props.buttons}
					onConditionChange={onConditionChange}
					criteria={props.criteria}
					property="ProductCategory"
					placeholder={placeholder}
					isDisabled={disableIfCriteriaEmpty(props.criteria)}
					getSegmentSuggestions={props.getSegmentSuggestions}
				/>
			);
		}
		return component;
	};

	const renderHowMany = () => {
		let component;
		if (fieldOptions && fieldOptions.IsTimesFilterSupported) {
			component = (
				<Box>
					<Input
						key="c"
						onChange={(e: any) => {
							if (e.target.value === '' || e.target.value >= 0) onConditionChange({ value: e.target.value, dataLabel: 'timesFilter' });
						}}
						value={props.timesFilter ? props.timesFilter : ''}
						id="filter-guid-input"
						placeholder="how many times?"
						disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
					/>
					<Description>Minimum number of times the event occurred or empty to ignore.</Description>
				</Box>
			);
		}
		return component;
	};

	const renderValueInput = () => {
		let component;
		const isProductField = fieldOptions && fieldOptions.IsProductFilterSupported;

		if (
			isBetweenComparer(props.comparer) &&
			R.find(R.propEq('value', props.dateFunction))(dateFunctionOptions) &&
			props.dateFunction !== 'NoTransform'
		) {
			const value = props.value ? props.value.split('|') : ['', ''];
			const onValueChange = (data: IInputCallbackData) => {
				if (data.dataLabel === 'start') value[0] = data.value;
				else value[1] = data.value;

				const dataToSend = {
					dataLabel: 'value',
					value: value.join('|'),
				};
				onConditionChange(dataToSend);
			};

			if (props.dateFunction && isSpecialDateTransform(props.dateFunction)) {
				const transformOptions = getDateTransformOptions(props.dateFunction);
				component = (
					<Box position="relative" d="flex" align="center" justify="space-between" style={{ marginBottom: '15px' }}>
						<Select
							onChangeSelect={(e, data) => onValueChange(data)}
							dataLabel="start"
							selectedValue={value[0]}
							className={styles.customW}
							disabled={props.segmentType?.includes('SystemGenerated')}
						>
							<SelectContainer>
								<SelectPlaceholder>Select an option</SelectPlaceholder>
							</SelectContainer>
							<SelectOptionList>
								{transformOptions?.map((item, i) => {
									return (
										<SelectOption key={i} value={item.value}>
											{item.label}
										</SelectOption>
									);
								})}
							</SelectOptionList>
						</Select>
						<Text fontSize={13} mx={10}>
							and
						</Text>
						<Select
							onChangeSelect={(e, data) => onValueChange(data)}
							dataLabel="end"
							selectedValue={value[1]}
							className={styles.customW}
							disabled={props.segmentType?.includes('SystemGenerated')}
						>
							<SelectContainer>
								<SelectPlaceholder>Select an option</SelectPlaceholder>
							</SelectContainer>
							<SelectOptionList>
								{transformOptions?.map((item, i) => {
									return (
										<SelectOption key={i} value={item.value}>
											{item.label}
										</SelectOption>
									);
								})}
							</SelectOptionList>
						</Select>
					</Box>
				);
			} else {
				component = (
					<Box d="flex" align="center" justify="center" flex={1} style={{ marginBottom: '15px' }}>
						<Input
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValueChange({ value: e.target.value, dataLabel: 'start' })}
							value={value[0]}
							type="text"
							disabled={props.segmentType?.includes('SystemGenerated')}
						/>
						<Text fontSize={13} mx={10}>
							and
						</Text>
						<Input
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValueChange({ value: e.target.value, dataLabel: 'end' })}
							value={value[1]}
							type="text"
							disabled={props.segmentType?.includes('SystemGenerated')}
						/>
					</Box>
				);
			}
		} else {
			switch (props.valueType) {
				case 'Boolean':
					if (!props.forSegments) {
						const booleanTags = props.value ? (Array.isArray(props.value) ? props.value : props.value.split('|')) : [];
						component = (
							<InputContainer flex={1} style={{ marginBottom: '15px' }} disabled={props.segmentType?.includes('SystemGenerated')}>
								<Tags onAddTag={(data) => onTagAdd(data)} onRemoveTag={(e, data) => onTagRemove(data, data.value)} dataLabel="value">
									{booleanTags.map((item: string) => {
										return (
											<Tag key={item} disabled={props.segmentType?.includes('SystemGenerated')}>
												<TagText>{item}</TagText>
											</Tag>
										);
									})}
									<TagInput
										pl={booleanTags.length ? 10 : 0}
										onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
										disabled={props.segmentType?.includes('SystemGenerated')}
									/>
								</Tags>
							</InputContainer>
						);
					}
					break;
				case 'Text': {
					const tagValues = props.value ? props.value.split('|') : [];
					let suggestions = stateSuggestions;
					const mailingListId = urlParams.id;
					const valueLabel = fieldOptions && fieldOptions.IsProductFilterSupported ? 'productCode' : 'value';
					const valueToShow =
						valueLabel === 'value' && !Array.isArray(props[valueLabel])
							? props[valueLabel]
								? props[valueLabel].split('|')
								: []
							: props[valueLabel];
					if (suggestions.length === 0) {
						component = (
							<ValueWrapper>
								<Box position="relative" style={{ marginBottom: '15px' }}>
									<InputContainer flex={1} disabled={props.segmentType?.includes('SystemGenerated')}>
										<Tags onAddTag={(data) => onTagAdd(data)} onRemoveTag={(e, data) => onTagRemove(data, data.value)} dataLabel="value">
											{tagValues.map((item: string) => {
												return (
													<Tag key={item} disabled={props.segmentType?.includes('SystemGenerated')}>
														<TagText>{item}</TagText>
													</Tag>
												);
											})}
											<TagInput
												pl={tagValues.length ? 10 : 0}
												onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
												disabled={disableIfCriteriaEmpty(props.criteria)}
												onBlur={onTagBlur}
											/>
										</Tags>
									</InputContainer>
									{props.isCustomValue && (
										<SelectIcon
											position="absolute"
											top={8}
											right={0}
											cursor="pointer"
											onClick={() => onConditionChange({ dataLabel: 'closeSuggestions', value: !props.isCustomValue })}
										>
											keyboard_arrow_down
										</SelectIcon>
									)}
								</Box>
							</ValueWrapper>
						);
						break;
					}
					// continue next case if there are suggestions
				}
				// no break it will continue into the next case
				case 'DropDown':
					if (isNotBooleanComparer) {
						if (props.dateFunction && isSpecialDateTransform(props.dateFunction)) {
							const dateValues = props.value ? props.value.split('|') : [];
							const transformOptions = getDateTransformOptions(props.dateFunction);
							component = (
								<ValueWrapper>
									<Select
										onChangeSelect={(e, data) => onMultiSelectChange(data)}
										dataLabel="value"
										selectedValue={dateValues}
										w="100%"
										style={{ marginBottom: '15px' }}
										disabled={props.segmentType?.includes('SystemGenerated')}
									>
										<SelectContainer>
											<SelectPlaceholder maxW={maxSuggestionWidth}>Select an option</SelectPlaceholder>
										</SelectContainer>
										<SelectOptionList>
											{transformOptions?.map((item, i) => {
												return (
													<SelectOption key={i} value={item.value}>
														{item.label}
													</SelectOption>
												);
											})}
										</SelectOptionList>
									</Select>
								</ValueWrapper>
							);
						} else {
							const tags = props.value ? props.value.split('|') : [];
							let suggestions = stateSuggestions;
							const valueLabel = fieldOptions && fieldOptions.IsProductFilterSupported ? 'productCode' : 'value';
							const valueToShow =
								valueLabel === 'value' && !Array.isArray(props[valueLabel])
									? props[valueLabel]
										? props[valueLabel].split('|')
										: []
									: props[valueLabel];

							const suggestionValues = suggestions.map((s) => s.Value).sort();
							const sortedValues = [...valueToShow].sort();
							const textWithoutSuggestions = suggestionValues.every((val, i) => val === sortedValues[i]);

							component =
								!props.isCustomValue && !isContainsDoesNotContainComparer && !textWithoutSuggestions ? (
									<ValueWrapper>
										<Select
											flex={1}
											dataLabel={valueLabel}
											onChangeSelect={(e, data) => onConditionChange(data)}
											selectedValue={!props[valueLabel] && fieldOptions?.IsProductFilterSupported ? 'any' : valueToShow}
											disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
										>
											<SelectContainer>
												<SelectPlaceholder maxW={props.buttons && props.buttons.length > 1 ? 213 : 275}>Select an option</SelectPlaceholder>
												<SelectFilter onChange={(e) => searchCustomFieldOptions(e.target.value)} />
											</SelectContainer>
											<SelectOptionList className="notranslate">
												{stateSuggestions.length > 0 &&
													stateSuggestions.map((item, i) => {
														const label = item.Label ? item.Label : item.Value;
														const value =
															(props.valueType === 'Guid' && props.criteria !== 'LinkURL') ||
															props.valueType === 'Enum' ||
															item.Key === '__supports_custom_value__'
																? item.Key
																: item.Value;
														return (
															<SelectOption key={i} value={value}>
																{label}
															</SelectOption>
														);
													})}
												{stateSuggestions.length === 0 &&
													suggestions.map((item, i) => {
														const label = item.Label ? item.Label : item.Value;
														const value =
															(props.valueType === 'Guid' && props.criteria !== 'LinkURL') ||
															props.valueType === 'Enum' ||
															item.Key === '__supports_custom_value__'
																? item.Key
																: item.Value;
														return (
															<SelectOption key={i} value={value}>
																{label}
															</SelectOption>
														);
													})}
											</SelectOptionList>
										</Select>
										{valueLabel === 'productCode' && <Description>{productCodeDesc}</Description>}
										{renderProductBrand()}
										{renderProductCategory()}
										{renderHowMany()}
									</ValueWrapper>
								) : (
									<ValueWrapper>
										<Box position="relative" style={{ marginBottom: '15px' }}>
											<InputContainer flex={1} disabled={props.segmentType?.includes('SystemGenerated')}>
												<Tags
													onAddTag={(data) => onTagAdd(data)}
													onRemoveTag={(e, data) => onTagRemove(data, data.value)}
													dataLabel="value"
												>
													{tags.map((item: string) => {
														return (
															<Tag key={item} disabled={props.segmentType?.includes('SystemGenerated')}>
																<TagText>{item}</TagText>
															</Tag>
														);
													})}
													<TagInput
														pl={tags.length ? 10 : 0}
														onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
														disabled={disableIfCriteriaEmpty(props.criteria)}
														onBlur={onTagBlur}
													/>
												</Tags>
											</InputContainer>
											{props.isCustomValue && (
												<SelectIcon
													position="absolute"
													top={8}
													right={0}
													cursor="pointer"
													onClick={() => onConditionChange({ dataLabel: 'closeSuggestions', value: !props.isCustomValue })}
												>
													keyboard_arrow_down
												</SelectIcon>
											)}
										</Box>
									</ValueWrapper>
								);
						}
					}
					break;

				case 'Guid':
					const tags = props.value ? props.value.split('|') : [];
					component = (
						<Box position="relative" style={{ marginBottom: '15px' }}>
							<InputContainer flex={1} disabled={props.segmentType?.includes('SystemGenerated')}>
								<Tags onAddTag={(data) => onTagAdd(data)} onRemoveTag={(e, data) => onTagRemove(data, data.value)} dataLabel="value">
									{tags.map((item: string) => {
										return (
											<Tag key={item}>
												<TagText>{item}</TagText>
											</Tag>
										);
									})}
									<TagInput
										pl={tags.length ? 10 : 0}
										onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
										disabled={disableIfCriteriaEmpty(props.criteria)}
										onBlur={onTagBlur}
									/>
								</Tags>
							</InputContainer>
							{props.isCustomValue && (
								<SelectIcon
									position="absolute"
									top={8}
									right={0}
									cursor="pointer"
									onClick={() => onConditionChange({ dataLabel: 'closeSuggestions', value: !props.isCustomValue })}
								>
									keyboard_arrow_down
								</SelectIcon>
							)}
						</Box>
					);
					break;

				case 'Integer':
					const integerTags = props.value ? props.value.split('|') : [];
					component = (
						<Box position="relative" style={{ marginBottom: '15px' }}>
							<InputContainer flex={1} disabled={props.segmentType?.includes('SystemGenerated')}>
								<Tags
									// TO DO
									// validateTag={(word: string) => Number.isInteger(Number(word))}
									onAddTag={(data) => onTagAdd(data)}
									onRemoveTag={(e, data) => onTagRemove(data, data.value)}
									dataLabel="value"
								>
									{integerTags.map((item: string) => {
										return (
											<Tag key={item}>
												<TagText>{item}</TagText>
											</Tag>
										);
									})}
									<TagInput
										pl={integerTags.length ? 10 : 0}
										onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
										disabled={disableIfCriteriaEmpty(props.criteria)}
										onBlur={onTagBlur}
									/>
								</Tags>
							</InputContainer>
							{props.isCustomValue && (
								<SelectIcon
									position="absolute"
									top={8}
									right={0}
									cursor="pointer"
									onClick={() => onConditionChange({ dataLabel: 'closeSuggestions', value: !props.isCustomValue })}
								>
									keyboard_arrow_down
								</SelectIcon>
							)}
						</Box>
					);
					break;

				case 'Decimal':
					const decimalTags = props.value ? props.value.split('|') : [];
					component = (
						<Box position="relative" style={{ marginBottom: '15px' }}>
							<InputContainer flex={1} mb={isProductField ? 15 : undefined} disabled={props.segmentType?.includes('SystemGenerated')}>
								<Tags
									// TO DO
									// validateTag={(word: string) => !isNaN(Number(word))}
									onAddTag={(data) => onTagAdd(data)}
									onRemoveTag={(e, data) => onTagRemove(data, data.value)}
									dataLabel="value"
								>
									{decimalTags.map((item: string) => {
										return (
											<Tag key={item}>
												<TagText>{item}</TagText>
											</Tag>
										);
									})}
									<TagInput
										pl={decimalTags.length ? 10 : 0}
										disabled={disableIfCriteriaEmpty(props.criteria)}
										onRemoveTag={(e) => onTagRemove(e, { dataLabel: 'value', index: -1 })}
										// placeholder={
										// 	fieldOptions && fieldOptions.IsProductFilterSupported && decimalTags.length === 0
										// 		? 'Enter a product price or leave empty for any price.'
										// 		: undefined
										// }
										onBlur={onTagBlur}
									/>
								</Tags>
							</InputContainer>
							{props.isCustomValue && (
								<SelectIcon
									position="absolute"
									top={8}
									right={0}
									cursor="pointer"
									onClick={() => onConditionChange({ dataLabel: 'closeSuggestions', value: !props.isCustomValue })}
								>
									keyboard_arrow_down
								</SelectIcon>
							)}
						</Box>
					);
					break;
				case 'DateTime':
				case 'DateTimeWithPrecision':
					if (isNotBooleanComparer) component = renderDatePicker();
					break;

				case 'Date':
					if (isNotBooleanComparer) component = renderDatePicker(true);
					break;

				case 'Enum':
					component = (
						<Input
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => onConditionChange({ value: e.target.value, dataLabel: 'value' })}
							value={props.value}
							flex={1}
							style={{ color: 'red' }}
							disabled={props.segmentType?.includes('SystemGenerated')}
						/>
					);
					break;
				default:
					component = (
						<Box d="flex" align="center" justify="center" py={10} flex={1} style={{ marginBottom: '15px' }}>
							Nothing Available!
						</Box>
					);
			}
		}
		return (
			<ValueWrapper>
				{component}
				{renderProductCode()}
				{renderProductBrand()}
				{renderProductCategory()}

				{renderHowMany()}
			</ValueWrapper>
		);
	};

	const renderWebsitesOptions = (websites: { Id: string; Domain: string }[]) => {
		const optionsToRender = [];

		const defaultOption = (
			<SelectOption key="zz22ka" value="any">
				in any website
			</SelectOption>
		);
		optionsToRender.push(defaultOption);

		websites.forEach((item, i) => {
			optionsToRender.push(
				<SelectOption key={getGUIDWithoutDashes(item.Id)} value={getGUIDWithoutDashes(item.Id)}>
					{item.Domain}
				</SelectOption>
			);
		});

		return optionsToRender;
	};

	const renderDateFuncOptions = () => {
		return dateFunctionOptions.map((item, i) => {
			return (
				<SelectOption key={i} value={item.value}>
					{item.label}
				</SelectOption>
			);
		});
	};

	const renderProductFields = (fieldOptionsArg: IFieldOptions) => {
		const fields = [];
		if (fieldOptionsArg && fieldOptionsArg.IsWebsiteFilterSupported) {
			fields.push(
				<Select
					key="a"
					dataLabel="website"
					onChangeSelect={(e, data) => onConditionChange(data)}
					selectedValue={props.website || 'any'}
					w="100%"
					disabled={disableIfCriteriaEmpty(props.criteria) || props.segmentType?.includes('SystemGenerated')}
				>
					<SelectContainer>
						<SelectPlaceholder>Select an option</SelectPlaceholder>
					</SelectContainer>
					<SelectOptionList>
						{props.websites ? (
							renderWebsitesOptions(props.websites)
						) : (
							<SelectOption key={'-1'} value="any">
								in any website
							</SelectOption>
						)}
					</SelectOptionList>
				</Select>
			);
		}
		if (fieldOptionsArg && fieldOptionsArg.IsDateFunctionSupported) {
			fields.push(
				<Select
					key="b"
					dataLabel="dateFunction"
					onChangeSelect={(e, data) => onConditionChange(data)}
					selectedValue={props.dateFunction || 'NoTransform'}
					w="100%"
					disabled={props.segmentType?.includes('SystemGenerated')}
				>
					<SelectContainer>
						<SelectPlaceholder>Select an option</SelectPlaceholder>
					</SelectContainer>
					<SelectOptionList>{renderDateFuncOptions()}</SelectOptionList>
				</Select>
			);
		}
		return fields;
	};

	const hasAdditionalOption = (FieldOptionsArg: IFieldOptions) => {
		const additionalOptions = Object.keys(FieldOptionsArg).find(
			(item) => item !== 'IsCrossMailingListField' && FieldOptionsArg[item as keyof IFieldOptions] === true
		);

		return additionalOptions?.length ? true : false;
	};

	const renderProductFieldsWrapper = () => {
		return fieldOptions
			? hasAdditionalOption(fieldOptions as IFieldOptions) && (
					<div style={{ display: 'flex', marginTop: 15 }}>{renderProductFields(fieldOptions as IFieldOptions)}</div>
			  )
			: undefined;
	};

	const disableIfCriteriaEmpty = (criteria: string) => {
		return props.fieldOptions?.IsDisabled ? true : criteria ? false : true;
	};

	return (
		<>
			<Box d="flex" align={props.forSegments ? 'flex-start' : undefined}>
				<Box flex={1} flexBasis="35%">
					<Select
						dataLabel="criteria"
						onChangeSelect={(e, data) => onConditionChange(data)}
						selectedValue={props.criteria}
						className="notranslate"
						style={{ marginBottom: '15px' }}
						disabled={props.segmentType?.includes('SystemGenerated')}
					>
						<SelectContainer>
							<SelectPlaceholder maxW={306}>{translateText('Select a criteria')}</SelectPlaceholder>
						</SelectContainer>
						<SelectOptionList>{renderOptions(props.criteriaOptions)}</SelectOptionList>
					</Select>
					{renderProductFieldsWrapper()}
				</Box>
				<Select
					mx={10}
					dataLabel="comparer"
					onChangeSelect={(e, data) => onConditionChange(data)}
					selectedValue={props.comparer}
					disabled={
						(props.fieldOptions?.IsDisabled ? true : props.criteria ? false : true) || props.segmentType?.includes('SystemGenerated')
					}
					flex={1}
					flexBasis="30%"
					className="notranslate"
					style={{ marginBottom: '15px' }}
				>
					<SelectContainer>
						<SelectPlaceholder>{translateText('Select a comparer')}</SelectPlaceholder>
					</SelectContainer>
					<SelectOptionList>{renderOptions(props.comparerOptions)}</SelectOptionList>
				</Select>
				<Box flex={1} d="flex" justify="flex-end" flexBasis="35%">
					{props.loading ? renderLoader() : props.suggestions && props.suggestions.length ? renderSuggestions() : renderValueInput()}
					{props.buttons && props.buttons.length && !props.segmentType?.includes('SystemGenerated')
						? renderButtonsContainer(props.buttons)
						: null}
				</Box>
			</Box>
			<Box d="flex" direction="column">
				{props.extraOption && renderExtraOptions()}
			</Box>
		</>
	);
};

const ValueWrapper: React.FC = (props) => {
	return (
		<Box d="flex" w="100%" direction="column">
			{props.children}
		</Box>
	);
};

const ExtraOptionsSelect: React.FC<{
	onConditionChange: (data: IInputCallbackData) => void;
	valueLabel: string;
	valueProp: any;
	buttons?: IConditionButton[];
	mailingListId?: string;
	criteria: IConditionFilter['criteria'];
	property: 'ProductBrand' | 'ProductCategory';
	placeholder: string;
	isDisabled: boolean;
	getSegmentSuggestions?: IFilterCollection['getSegmentSuggestions'];
}> = (props) => {
	const { onConditionChange, valueLabel, valueProp, mailingListId, criteria, property, placeholder, isDisabled, getSegmentSuggestions } =
		props;
	const { data, isLoading } = useQuery(
		[valueLabel],
		() => {
			return getSegmentSuggestions && getSegmentSuggestions(criteria, { mailingListId, property });
		},
		{ keepPreviousData: true }
	);

	if (isLoading || !data) {
		return null;
	}

	const suggestions: IConditionSuggestion[] = data.data.Context || [];
	const val = valueProp ? valueProp.split('|') : [];

	const onNewConditionChange = (e: any, data: IInputCallbackData) => {
		const newData = {
			...data,
			value: data.value.join('|'),
		};
		onConditionChange(newData);
	};

	return (
		<Select flex={1} mt={15} dataLabel={valueLabel} onChangeSelect={onNewConditionChange} selectedValue={val} disabled={isDisabled}>
			<SelectContainer>
				<SelectPlaceholder title={val.join(', ')} maxW={props.buttons && props.buttons.length > 1 ? 213 : 275}>
					{placeholder}
				</SelectPlaceholder>
			</SelectContainer>
			<SelectOptionList className="notranslate">
				{suggestions.map((item, i) => {
					const label = item.Label ? item.Label : item.Value;
					const value = item.Key === '__supports_custom_value__' ? item.Key : item.Value;

					if (item.Key === '__supports_custom_value__') {
						return null;
					}
					return (
						<SelectOption key={i} value={value}>
							{label}
						</SelectOption>
					);
				})}
			</SelectOptionList>
		</Select>
	);
};

export default Filter;
